import { Box } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { useState } from 'react';
import { ContentDimensions, VariableValue } from '@/core';
import { SPLIT_VARIABLE_PANEL_KEY_DELIMITER, SPLIT_VARIABLE_VALUE_DELIMITER } from '@/dashboards/constants';
import {
  PanelGroupItemId,
  useEditMode,
  usePanel,
  usePanelActions,
  usePanelEditor,
  usePanelGroup,
  useUpdateExpandPanelParams,
} from '@/dashboards/context';
import { useExpandPanelContext } from '@/plugin-system';
import { InViewContext, useInView } from '@/plugin-system/runtime/in-view';
import { Panel, PanelOptions, PanelProps, PanelProvider } from '../Panel';

export interface GridLayoutDimensions {
  w: number;
  h: number;
}

export interface GridItemContentProps extends ContentDimensions {
  panelGroupItemId: PanelGroupItemId;
  panelOptions?: PanelOptions;
  PanelProps?: Pick<PanelProps, 'renderTitle'>;
  gridLayout?: GridLayoutDimensions;
  splitVariableValue?: VariableValue;
}

/**
 * Resolves the reference to panel content in a GridItemDefinition and renders the panel.
 */
export function GridItemContent(props: GridItemContentProps) {
  const { panelGroupItemId, splitVariableValue, width, height } = props;
  const { panelKey: initialPanelKey, panelDefinition } = usePanel(panelGroupItemId);
  const group = usePanelGroup(panelGroupItemId.panelGroupId);

  const { enableDashboardUndoNotification } = useFeatureFlags();

  // Ensure each panelKey is unique when split by variable is used to create dynamic panel groups.
  const panelKey =
    group.splitVariableName && splitVariableValue
      ? `${initialPanelKey}${SPLIT_VARIABLE_PANEL_KEY_DELIMITER}${group.splitVariableName}${SPLIT_VARIABLE_VALUE_DELIMITER}${splitVariableValue}`
      : initialPanelKey;

  const { openEditPanel, deletePanel, openDeletePanelDialog, duplicatePanel, editPanelDefinition } =
    usePanelActions(panelGroupItemId);
  const panelEditor = usePanelEditor();

  const { isEditMode } = useEditMode();
  const [prevIsEditMode, setPrevIsEditMode] = useState(isEditMode);

  const [viewPanelDefinition, setViewPanelDefinition] = useState(panelDefinition);

  // sync viewPanelDefinition with one saved in the store every time we toggle between edit and view modes
  if (isEditMode !== prevIsEditMode) {
    setViewPanelDefinition(panelDefinition);
    setPrevIsEditMode(isEditMode);
  }

  const inViewContext = useInView({
    threshold: 0.01, // we have the flexibility to adjust this threshold to trigger queries slightly earlier or later based on performance
    initialInView: false,
  });

  const { hasBeenInView, ref, isInView } = inViewContext;

  const updateExpandPanelParams = useUpdateExpandPanelParams();

  // Provide actions to the panel when in edit mode
  let editHandlers: PanelProps['editHandlers'] = undefined;
  if (isEditMode) {
    editHandlers = {
      onEditPanelClick: () => {
        openEditPanel();
        updateExpandPanelParams({ editPanel: panelKey });
      },
    };
    // If the panel is in a grid, we can also duplicate and delete it
    if (group.kind === 'Grid') {
      editHandlers = {
        ...editHandlers,
        onDuplicatePanelClick: duplicatePanel,
        onDeletePanelClick: enableDashboardUndoNotification ? deletePanel : openDeletePanelDialog,
      };
    }
  }

  const handlePanelDefinitionChange: PanelProps['onDefinitionChange'] = (definition) => {
    // if in edit mode, update the store, else update internal state only
    if (isEditMode) {
      editPanelDefinition(definition);
    } else {
      setViewPanelDefinition(definition);
    }
  };

  const { expandedPanelKey } = useExpandPanelContext();
  const isFullScreenOpen = Boolean(expandedPanelKey);
  // We only want to enable queries when the panel is in view and not in fullscreen mode.
  // Account for dynamic panels using split panel group (see no data bug sc-133795).
  const queriesEnabled = (group.splitVariableName !== undefined ? hasBeenInView : isInView) && !isFullScreenOpen;

  // Check if the current panel was expanded and is now behind the dialog.
  // If so, offload this panel so that there is not a duplicate panelKey in the DOM.
  // expandedPanelKey is set when view tab is open, panelEditor.panelKey is set when edit tab is open.
  const isOriginalPanelBehindExpandedDialog = expandedPanelKey === panelKey || panelEditor?.panelKey === panelKey;
  const showPanel = hasBeenInView && !isOriginalPanelBehindExpandedDialog;

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {showPanel ? (
        <PanelProvider panelDefinition={panelDefinition} panelKey={panelKey} disableQueries={!queriesEnabled}>
          <InViewContext.Provider value={inViewContext}>
            {props.panelOptions?.renderPanel ? (
              props.panelOptions.renderPanel({
                ...props.PanelProps,
                mode: isEditMode ? 'edit' : 'view',
                definition: isEditMode ? panelDefinition : viewPanelDefinition,
                editHandlers,
                draggable: group.kind === 'Grid',
                panelGroupItemId,
                panelKey,
                panelOptions: props.panelOptions,
                onDefinitionChange: handlePanelDefinitionChange,
                width,
                height,
                gridLayout: props.gridLayout,
              })
            ) : (
              <Panel
                {...props.PanelProps}
                mode={isEditMode ? 'edit' : 'view'}
                definition={isEditMode ? panelDefinition : viewPanelDefinition}
                editHandlers={editHandlers}
                draggable={group.kind === 'Grid'}
                panelGroupItemId={panelGroupItemId}
                panelKey={panelKey}
                panelOptions={props.panelOptions}
                onDefinitionChange={handlePanelDefinitionChange}
                width={width}
                height={height}
              />
            )}
          </InViewContext.Provider>
        </PanelProvider>
      ) : (
        <Box border={1} borderColor={(t) => t.palette.divider} padding={1} height={'100%'}>
          {panelDefinition.spec.display.name}
        </Box>
      )}
    </div>
  );
}
